import isFunction from "lodash/isFunction";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import AnalyticsFactory from "./AnalyticsFactory";
import get from "lodash/get";
import keys from "lodash/keys";
import find from "lodash/find";
import includes from "lodash/includes";
import { forms } from "utils/constants";
import head from "lodash/head";
import { emailRegex } from "validations";
import { getChannelFromUrl } from "utils/helpers";

const FIELD_BLURRED = "field_blurred";
const FIELD_CHANGED = "field_changed";
const PUSH_PROFILE_EVENTS = [
  "field_blurred",
  "SelfActivationForm_submitted",
  "loggedInUserDetails_api_call_succeeded",
  "validateIccId_api_call_succeeded"
];
const BLUR_MONITORED_FIELDS = ["email", "contact_number", "telco"];
const CHANGE_MONITORED_FIELDS = [
  "telco",
  "registered_under_own_nric_id",
  "port_in_existing_plan_type",
  "number_type"
];

const SET_SUBMIT = "submitted";
const SET_SUBMIT_SUCCEEDED_FORMS = [
  forms.PERSONAL_DETAILS_FORM,
  forms.DELIVERY_INFO_FORM,
  forms.PORT_IN_FORM,
  forms.NEW_NUMBER_FORM,
  forms.PRE_CHECKOUT_FORM,
  forms.SELF_ACTIVATION_FORM,
  forms.PHONES_FORM
];

function getSuccessStatus(key, data) {
  switch (key) {
    case "paymentStatus":
    case "paymentReturn": {
      const statusValue = get(data, "payment_status", "");
      return statusValue.toLocaleLowerCase() === "success";
    }
    default: {
      return true;
    }
  }
}

let reduxStore = {};

const getStoreDataForChargedEvent = orderParams => {
  const { activation_charge = 0 } = getStateData(
    reduxStore,
    "api.planService.data",
    {}
  );

  const formName = !isEmpty(
    getStateData(reduxStore, "form.DeliveryInfoForm.values", {})
  )
    ? forms.DELIVERY_INFO_FORM
    : !isEmpty(getStateData(reduxStore, "form.SelfActivationForm.values", {}))
    ? forms.SELF_ACTIVATION_FORM
    : forms.PERSONAL_DETAILS_FORM;

  const {
    delivery_slot_full_data,
    delivery_slot_fee = 0,
    delivery_method
  } = getStateData(reduxStore, `form.${formName}.values`, {});

  return {
    ...orderParams,
    ...(isEmpty(get(orderParams, "order_summary", {})) && {
      order_summary: {
        onetime_total_fee: (activation_charge || 0) + (delivery_slot_fee || 0)
      }
    }),
    ...(!isObject(get(orderParams, "delivery_slot")) &&
      delivery_slot_full_data && {
        delivery_slot: {
          delivery_method: get(delivery_method, "action"),
          delivery_slot_full_data
        }
      })
  };
};

/**
 * This is called saga file where saga fill invoke handleAnalytics on every action
 *
 * @param {Object} action
 */
export const handleAnalytics = action => {
  const { type } = action;
  const handler = analyticsConstructor[type];
  if (handler && isFunction(handler)) {
    const eventData = handler(action, reduxStore);
    if (!isEmpty(eventData)) {
      const { event, data, message } = eventData;
      if (includes(PUSH_PROFILE_EVENTS, event)) {
        AnalyticsFactory.pushProfile(event, data, message);
      }
      AnalyticsFactory.pushEvent(event, data, message);
    }
  }
};

/**
 * handleAnalytics method will pick the correct action from the analyticsConstructor and returns event name and data object.
 *
 * @returns {{ event: String, data: Object }}
 */
const analyticsConstructor = {
  FETCH_REQUESTED: ({ payload = {} }) => {
    const { key, params = {} } = payload;
    const planService = getStateData(reduxStore, "api.planService.data", {});
    const validateIccIdData = getStateData(
      reduxStore,
      "api.validateIccId.data",
      {}
    );
    const planName =
      planService?.name || validateIccIdData?.plan?.name || getChannelFromUrl();
    const selected_number = getStateData(
      reduxStore,
      "api.numbers.data.selected_number"
    );
    const referral_code_type = getStateData(
      reduxStore,
      "api.referralCode.data.code_type",
      ""
    );
    const order_ref = get(validateIccIdData, "order.order_ref");
    return {
      event: `${key}_api_call_requested`,
      data: {
        ...params,
        plan_name: planName,
        selected_number,
        referral_code_type,
        order_ref
      }
    };
  },
  FETCH_SUCCEEDED: ({ payload = {} }) => {
    const { key, data } = payload;
    const planService = getStateData(reduxStore, "api.planService.data", {});
    const validateIccIdData = getStateData(
      reduxStore,
      "api.validateIccId.data",
      {}
    );
    const plan_name =
      planService?.name || validateIccIdData?.plan?.name || getChannelFromUrl();

    if (!getSuccessStatus(key, data)) return {};

    const userOrder =
      head(getStateData(reduxStore, "api.userOrders.data")) || {};
    let orderParamsData =
      getStateData(reduxStore, "api.order.params") ||
      getStateData(reduxStore, "api.selfActivationDeliveryOrder.params");
    let orderParams = !isEmpty(orderParamsData?.order)
      ? orderParamsData?.order
      : orderParamsData;
    orderParams = getStoreDataForChargedEvent(orderParams);
    const order = getStateData(reduxStore, "api.order.data");
    const referral_code = getStateData(
      reduxStore,
      "api.referralCode.data.referral_code",
      ""
    );
    const referral_code_type = getStateData(
      reduxStore,
      "api.referralCode.data.code_type",
      ""
    );
    const forms = getStateData(reduxStore, "form", {});
    const currency = getStateData(
      reduxStore,
      "api.appSettings.data.currency.currency_code",
      ""
    );
    const selected_number = getStateData(
      reduxStore,
      "api.numbers.data.selected_number"
    );
    const order_ref = get(validateIccIdData, "order.order_ref");
    return {
      event: `${key}_api_call_succeeded`,
      data: {
        planService,
        plan_name,
        referral_code,
        referral_code_type,
        userOrder,
        forms,
        order,
        orderParams,
        currency,
        selected_number,
        order_ref,
        ...data
      }
    };
  },
  FETCH_FAILED: ({ payload = {} }) => {
    const { key, error } = payload;
    return {
      event: `${key}_api_call_failed`,
      data: { error }
    };
  },
  "@@redux-form/CHANGE": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    const planService = getStateData(reduxStore, "api.planService.data", {});
    const validateIccIdData = getStateData(
      reduxStore,
      "api.validateIccId.data",
      {}
    );
    const planName =
      planService?.name || validateIccIdData?.plan?.name || getChannelFromUrl();
    if (CHANGE_MONITORED_FIELDS.includes(field) && payload) {
      const formValues = getStateData(reduxStore, `form.${form}.values`, {});

      const selected_number =
        formValues?.number_type === "new_number"
          ? getStateData(reduxStore, "api.numbers.data.selected_number.number")
          : getStateData(reduxStore, "form.PortInForm.values.post_paid_number");

      if (selected_number) {
        formValues["new_selected_number"] = selected_number;
      }

      const referral_code_type = getStateData(
        reduxStore,
        "api.referralCode.data.code_type",
        ""
      );

      const order_ref = get(validateIccIdData, "order.order_ref");

      return {
        event: `${FIELD_CHANGED}_${field}`,
        data: {
          form,
          field,
          payload,
          formValues,
          plan_name: planName,
          selected_number,
          referral_code_type,
          order_ref
        }
      };
    }
  },
  "@@redux-form/BLUR": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    if (BLUR_MONITORED_FIELDS.includes(field) && payload) {
      const formValues = getStateData(reduxStore, `form.${form}.values`, {});
      const loggedInUserEmail = getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.email",
        ""
      );
      const referralCodeData = getStateData(
        reduxStore,
        "api.referralCode.data",
        {}
      );

      if (loggedInUserEmail) {
        formValues["loggedInUserEmail"] = loggedInUserEmail;
      }

      if (!isEmpty(referralCodeData)) {
        formValues["referral_code_type"] = referralCodeData?.code_type || "";
        formValues["referral_code"] = referralCodeData?.referral_code || "";
      }

      // making sure only valid emails are sent as events
      if (field === "email" && !isValidEmail(formValues.email)) {
        return;
      }

      formValues[field] = payload;
      return {
        event: FIELD_BLURRED,
        data: { form, field, payload, formValues }
      };
    }
  },
  "@@redux-form/SET_SUBMIT_SUCCEEDED": ({ type, meta = {} }) => {
    const { form } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    if (SET_SUBMIT_SUCCEEDED_FORMS.includes(form)) {
      return {
        event: `${form}_${SET_SUBMIT}`,
        data: { form, formValues }
      };
    }
  },
  SET_DATA: ({ payload = {} }, reduxStore) => {
    const { key, data } = payload;
    const selectedKey = keys(data)[0];
    const additionalProducts = getStateData(
      reduxStore,
      "api.planService.data.additional_products"
    );
    const validateIccIdData = getStateData(
      reduxStore,
      "api.validateIccId.data",
      {}
    );
    const manageOrderAction =
      key === "guestOrder"
        ? {
            button_name: getStateData(
              reduxStore,
              `api.locale.data.manage_order_${data?.[selectedKey]}`,
              ""
            ),
            action_name: data?.[selectedKey]
          }
        : {};
    const addon = find(additionalProducts, { id: selectedKey });

    const plan_name =
      getStateData(reduxStore, "api.planService.data.name", "") ||
      validateIccIdData?.plan?.name ||
      getChannelFromUrl();
    let orderParamsData =
      getStateData(reduxStore, "api.order.params") ||
      getStateData(reduxStore, "api.selfActivationDeliveryOrder.params");
    let orderParams = !isEmpty(orderParamsData?.order)
      ? orderParamsData?.order
      : orderParamsData;
    orderParams = getStoreDataForChargedEvent(orderParams);
    let sadOrder = getStateData(
      reduxStore,
      "api.selfActivationDeliveryOrder.data"
    );
    let order = getStateData(reduxStore, "api.order.data");
    order = order?.order || order || sadOrder;
    const referral_code = getStateData(
      reduxStore,
      "api.referralCode.data.referral_code",
      ""
    );
    const referral_code_type = getStateData(
      reduxStore,
      "api.referralCode.data.code_type",
      ""
    );
    const planService = getStateData(reduxStore, "api.planService.data", {});
    const forms = getStateData(reduxStore, "form", {});
    const currency = getStateData(
      reduxStore,
      "api.appSettings.data.currency.currency_code",
      ""
    );
    const selected_number = getStateData(
      reduxStore,
      "api.numbers.data.selected_number"
    );
    const order_ref = get(validateIccIdData, "order.order_ref");
    return {
      event: `${key}_requested`,
      data: {
        ...data,
        planService,
        forms,
        currency,
        plan_name,
        addon,
        self_activation: true,
        referral_code,
        referral_code_type,
        selected: data?.[selectedKey],
        ...manageOrderAction,
        order,
        orderParams,
        selected_number,
        order_ref
      }
    };
  }
};

export const currencyCode = () => {
  return getStateData(
    reduxStore,
    "api.appSettings.data.currency.currency_code"
  );
};

export const initPushNotification = store => {
  try {
    reduxStore = store;
    const stateAPI = getStateData(store, "api");
    const clevertapNotificationsConfig = get(
      stateAPI,
      "appSettings.data.plugins.clevertap.data.notifications"
    );
    const notificationDelay = clevertapNotificationsConfig?.delay || 3000;
    const notificationEnabled = clevertapNotificationsConfig?.enable;

    const translations = get(stateAPI, "locale.data", {});
    setTimeout(() => {
      if (!window.clevertapDisabled && window.clevertap) {
        if (notificationEnabled) {
          window.clevertap.notifications.push({
            titleText: translations.clevertap_webPushTitleText,
            bodyText: translations.clevertap_webPushBodyText,
            okButtonText: translations.clevertap_webPushOkButtonText,
            rejectButtonText: translations.clevertap_webPushRejectButtonText,
            okButtonColor: "#f28046",
            hidePoweredByCT: true
          });
          console.log("CleverTap notification initialized");
        } else {
          console.log("CleverTap notification not enabled");
        }
      } else {
        console.log("CleverTap is undefined. Skipping push notification");
      }
    }, Number(notificationDelay));
  } catch (e) {
    console.error(
      "An error encountered during clevertap notifications initialization"
    );
    console.error(e);
  }
};

const getStateData = (store, path, defaultValue) => {
  let state = {};
  if (isFunction(store?.getState)) {
    state = store.getState();
  }
  return get(state, path, defaultValue);
};

const isValidEmail = value => {
  return new RegExp(emailRegex, "i").test(value);
};
